import io from 'socket.io-client';
import GetValidatedTokenData from '../utils/helper';

const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const CONNECTION_STATES = {
  DISCONNECTED: 'DISCONNECTED',
  CONNECTING: 'CONNECTING',
  CONNECTED: 'CONNECTED',
  RECONNECTING: 'RECONNECTING',
};

const MAX_RETRIES = 10;
const BASE_RETRY_DELAY = 1000;
const MAX_RETRY_DELAY = 30000;
const PING_INTERVAL = 15000;
const SYNC_STORAGE_KEY = 'chat_last_sync_timestamp';

class SocketClient {
  static instance = null;
  socket = null;
  retries = 0;
  pendingMessages = [];
  pingInterval = null;
  connectionState = CONNECTION_STATES.DISCONNECTED;
  stateListeners = new Set();
  messageListeners = new Map();
  syncListeners = new Set();
  organizationId = null;
  reconnectTimer = null;

  constructor() {
    if (SocketClient.instance) {
      return SocketClient.instance;
    }
    SocketClient.instance = this;
  }

  getConnectionState() {
    return this.connectionState;
  }

  onStateChange(listener) {
    this.stateListeners.add(listener);
    return () => this.stateListeners.delete(listener);
  }

  _setConnectionState(newState) {
    if (this.connectionState !== newState) {
      this.connectionState = newState;
      this.stateListeners.forEach((fn) => {
        try {
          fn(newState);
        } catch (_) {
          /* ignore */
        }
      });
    }
  }

  onSyncComplete(listener) {
    this.syncListeners.add(listener);
    return () => this.syncListeners.delete(listener);
  }

  _notifySyncComplete(syncData) {
    this.syncListeners.forEach((fn) => {
      try {
        fn(syncData);
      } catch (_) {
        /* ignore */
      }
    });
  }

  getLastSyncTimestamp() {
    return localStorage.getItem(SYNC_STORAGE_KEY) || null;
  }

  setLastSyncTimestamp(ts) {
    localStorage.setItem(SYNC_STORAGE_KEY, ts);
  }

  setOrganizationId(orgId) {
    this.organizationId = orgId;
  }

  async initSocket() {
    if (this.socket?.connected) {
      return this.socket;
    }

    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    const currentUserInfo = GetValidatedTokenData();
    if (!currentUserInfo?.id) {
      return null;
    }

    const token = localStorage.getItem('Token');

    this._setConnectionState(CONNECTION_STATES.CONNECTING);

    this.socket = io(process.env.REACT_APP_WEBSOCKET_URL, {
      query: {
        userId: currentUserInfo.id,
        token: token,
        deviceInfo: navigator.userAgent,
      },
      transports: ['websocket', 'polling'],
      reconnection: false,
      timeout: 10000,
    });

    this._setupListeners();
    return this.socket;
  }

  _setupListeners() {
    this.socket.on('connect', () => {
      console.log('[MSG] Frontend socket CONNECTED:', this.socket.id);
      this.retries = 0;
      this._clearReconnectTimer();
      this._setConnectionState(CONNECTION_STATES.CONNECTED);
      this._flushPendingMessages();
      this._startPingInterval();
      this._reattachListeners();
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[MSG] Frontend socket DISCONNECTED:', reason);
      this._stopPingInterval();
      this._setConnectionState(CONNECTION_STATES.DISCONNECTED);
      if (reason !== 'io server disconnect') {
        this._scheduleReconnect();
      }
    });

    this.socket.on('connect_error', (err) => {
      console.log('[MSG] Frontend socket connect_error:', err?.message);
      this._stopPingInterval();
      this._setConnectionState(CONNECTION_STATES.RECONNECTING);
      this._scheduleReconnect();
    });

    this.socket.on('authError', () => {
      this._setConnectionState(CONNECTION_STATES.DISCONNECTED);
      this.disconnect();
    });

    this.socket.onAny((event, ...args) => {
      if (event !== 'ping' && event !== 'pong') {
        console.log(
          '[MSG] RAW EVENT received:',
          event,
          args?.[0]?.[0]?.message?.substring?.(0, 30) || '',
        );
      }
    });

    this.socket.on('sync:required', () => {
      this._requestSync();
    });

    this.socket.on('sync:response', (data) => {
      if (data.error) return;
      if (data.syncTimestamp) {
        this.setLastSyncTimestamp(data.syncTimestamp);
      }
      this._notifySyncComplete(data);
    });
  }

  _reattachListeners() {
    const summary = [];
    for (const [event, callbacks] of this.messageListeners.entries()) {
      summary.push(`${event}(${callbacks.size})`);
      this.socket.off(event); // Remove old listeners to prevent duplicates on reconnect
      for (const cb of callbacks) {
        this.socket.on(event, cb);
      }
    }
    console.log('[MSG] Reattached listeners:', summary.join(', ') || 'none');
  }

  _requestSync() {
    const since = this.getLastSyncTimestamp();
    if (!since || !this.organizationId) {
      this.setLastSyncTimestamp(new Date().toISOString());
      return;
    }
    if (this.socket?.connected) {
      this.socket.emit('sync:request', {
        since: since,
        organizationId: this.organizationId,
      });
    }
  }

  _scheduleReconnect() {
    if (this.reconnectTimer) return;
    if (this.retries >= MAX_RETRIES) {
      this._setConnectionState(CONNECTION_STATES.DISCONNECTED);
      return;
    }

    this.retries++;
    const delay = Math.min(BASE_RETRY_DELAY * Math.pow(2, this.retries - 1), MAX_RETRY_DELAY);
    const jitter = delay * 0.2 * Math.random();
    const totalDelay = delay + jitter;

    this._setConnectionState(CONNECTION_STATES.RECONNECTING);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.socket) {
        this.socket.connect();
      }
    }, totalDelay);
  }

  _clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  _startPingInterval() {
    this._stopPingInterval();
    this.pingInterval = setInterval(() => {
      if (this.socket?.connected) {
        this.socket.emit('ping');
      }
    }, PING_INTERVAL);
  }

  _stopPingInterval() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  _flushPendingMessages() {
    if (this.socket?.connected) {
      const batch = [...this.pendingMessages];
      this.pendingMessages = [];
      for (const { event, data } of batch) {
        this.socket.emit(event, data);
      }
    }
  }

  async getSocket() {
    if (!this.socket || !this.socket.connected) {
      await this.initSocket();
    }
    return this.socket;
  }

  generateMessageId() {
    return generateUUID();
  }

  async emit(event, data) {
    console.log('[MSG] EMIT:', event, data?.message?.substring?.(0, 30) || data?.groupId || '');
    const socket = await this.getSocket();
    if (socket?.connected) {
      socket.emit(event, data);
    } else {
      console.log('[MSG] Socket not connected, queuing:', event);
      this.pendingMessages.push({ event, data });
    }
  }

  on(event, callback) {
    if (!this.messageListeners.has(event)) {
      this.messageListeners.set(event, new Set());
    }
    this.messageListeners.get(event).add(callback);
    if (this.socket) {
      this.socket.on(event, callback);
    }
    console.log(
      '[MSG] ON registered:',
      event,
      'total listeners:',
      this.messageListeners.get(event).size,
      'socket exists:',
      !!this.socket,
    );
  }

  off(event, callback) {
    if (this.socket) {
      this.socket.off(event, callback);
    }
    const listeners = this.messageListeners.get(event);
    if (listeners) {
      listeners.delete(callback);
      console.log('[MSG] OFF removed:', event, 'remaining:', listeners.size);
    }
  }

  isConnected() {
    return this.socket?.connected || false;
  }

  disconnect() {
    this._stopPingInterval();
    this._clearReconnectTimer();
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this._setConnectionState(CONNECTION_STATES.DISCONNECTED);
    this.retries = 0;
    this.pendingMessages = [];
    this.messageListeners.clear();
  }

  resetRetries() {
    this.retries = 0;
  }
}

const socketClient = new SocketClient();
export { CONNECTION_STATES };
export default socketClient;
